import {useContext, ReactNode} from 'react';
import {TypeImplementation} from '@packages/systems/wfdl';
import {type ContextualExpression} from '@packages/utilities/contextual-expression';
import type {ExpressionEditorDispatch} from '@packages/systems/ExpressionEditor-utils';
import {CustomControls} from './CustomControlsContext';

export type LabelControlsType = Array<LabelItem>;

type LabelItem = {
  /**
   * The main function that is used to determine if a LabelItemResult is returned
   * or not
   */
  condition: (
    /**
     * The ContextualExpression at the point of the label
     */
    expr: ContextualExpression,
    /**
     * An ExpressionEditor provided dispatch function. For usage examples look at
     * packages/systems/ExpressionEditor-utils/index.js
     */
    dispatch: ExpressionEditorDispatch,
    /**
     * eeContext is a string denoting in which context (overrides, logic, etc) the
     * current ExpressionEditor is rendered
     */
    eeContext: TypeImplementation.DataTypeEnvironmentContext
  ) => LabelItemResult | null;
};

export type LabelItemResult = {
  /**
   * A flag that signifies that this label should have a warning color
   */
  hasWarning: boolean;
  /**
   * An optional section for some non-clickable content for informational purposes,
   * rendered above the clickable section
   */
  content: ReactNode | null;
  /**
   * An optional clickable section, contains some content and the click handler
   * that is called when said content is clicked
   */
  clickable: {
    content: ReactNode | null;
    onClick: () => void;
  } | null;
};

export const useLabelItems = (
  expr: ContextualExpression,
  dispatch: ExpressionEditorDispatch,
  eeContext: TypeImplementation.DataTypeEnvironmentContext
): Array<LabelItemResult> => {
  const controls = useContext(CustomControls);
  const labelHandlers = controls.LabelControls;

  const items: Array<LabelItemResult> = [];
  labelHandlers.forEach((handler) => {
    const res = handler.condition(expr, dispatch, eeContext);
    if (res !== null) {
      items.push(res);
    }
  });
  return items;
};
